/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    IApplicationLog,
    IConnectionLog,
    IWafRuleLog,
} from 'generated-types';

import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';
import { TrafficLogListStore } from '../traffic-log-list/traffic-log-list.store';
import { ILogCinematicStates, LogCinematicStore } from '../../log-cinematic/log-cinematic.store';
import { TWafRuleLogsByGroup } from './traffic-log-cinematic.types';

import { isObjectEmpty } from '../../../utils/logs.utils';

interface ITrafficLogCinematicStates extends Omit<ILogCinematicStates, 'eventLog'> {
    applicationLog: IApplicationLog;
    connectionLog: IConnectionLog;
}

const initialState: ITrafficLogCinematicStates = {
    applicationLog: {},
    connectionLog: {},
    hasPreviousLog: false,
    hasNextLog: false,
};

/**
 * @description
 *     Abstract store for managing the state and effects for TrafficLogCinematicComponent and its children components.
 *     Should never be used as a direct provider for a component.
 *     The @Injectable() notation is only used to validate the implementation of Angular OnDestroy interface.
 *     A more concrete class must be created to provide the module specific LogListStore and TrafficLogsStore.
 * @author Zhiqian Liu
 */
@Injectable()
export class TrafficLogCinematicStore extends LogCinematicStore<ITrafficLogCinematicStates> implements OnDestroy {
    public readonly applicationLog$ = this.select(state => state.applicationLog);

    public readonly connectionLog$ = this.select(state => state.connectionLog);

    /**
     * Implementation of the parent class abstractlog$ selector.
     * Select the log loaded.
     * If nothing loaded, an empty object is guaranteed to be returned.
     */
    public override readonly log$ = this.select(
        this.applicationLog$,
        this.connectionLog$,
        (applicationLog, connectionLog) => {
            return !isObjectEmpty(applicationLog) ? applicationLog : connectionLog;
        },
    );

    /**
     * Indicate whether a Log has Avi defined filters.
     */
    public readonly isSignificantLog$ = this.select(this.log$, ({ adf }) => adf);

    /**
     * Indicate whether a Log has user defined filters.
     */
    public readonly isUserDefinedLog$ = this.select(this.log$, ({ udf }) => udf);

    // ******************************** Generic Log Selectors ************************************

    public readonly timestamp$ = this.select(
        this.log$,
        ({ report_timestamp }) => report_timestamp,
    );
    public readonly clientRtt$ = this.select(this.log$, ({ client_rtt }) => client_rtt);

    public readonly serverRtt$ = this.select(this.log$, ({ server_rtt }) => server_rtt);

    public readonly totalTime$ = this.select(this.log$, ({ total_time }) => (
        total_time ? Number(total_time) : total_time
    ));

    public readonly pool$ = this.select(this.log$, ({ pool }) => pool);

    public readonly poolName$ = this.select(this.log$, ({ pool_name }) => pool_name);

    public readonly clientIps$ = this.select(
        this.log$, ({ client_ip6, client_ip }) => {
            return {
                client_ip,
                client_ip6,
            };
        },
    );

    public readonly clientSrcPort$ = this.select(
        this.log$,
        ({ client_src_port }) => client_src_port,
    );

    public readonly clientDestPort$ = this.select(
        this.log$,
        ({ client_dest_port }) => client_dest_port,
    );

    public readonly vsIps$ = this.select(
        this.log$, ({ vs_ip, vs_ip6 }) => {
            return {
                vs_ip,
                vs_ip6,
            };
        },
    );

    public readonly serverName$ = this.select(
        this.log$,
        ({ server_name }) => server_name,
    );

    public readonly serverIps$ = this.select(
        this.log$, ({ server_ip, server_ip6 }) => {
            return {
                server_ip,
                server_ip6,
            };
        },
    );

    public readonly serverSrcPort$ = this.select(
        this.log$, ({ server_src_port }) => server_src_port,
    );

    public readonly serverDestPort$ = this.select(
        this.log$, ({ server_dest_port }) => server_dest_port,
    );

    public readonly serverConnectionSrcIps$ = this.select(
        this.log$,
        ({ server_conn_src_ip, server_conn_src_ip6 }) => {
            return {
                server_conn_src_ip,
                server_conn_src_ip6,
            };
        },
    );

    public readonly serviceEngine$ = this.select(
        this.log$, ({ service_engine }) => service_engine,
    );

    public readonly vcpuId$ = this.select(this.log$, ({ vcpu_id }) => vcpu_id);

    public readonly significance$ = this.select(this.log$, ({ significance }) => significance);

    public readonly significantLog$ = this.select(
        this.log$, ({ significant_log: significanceLog }) => significanceLog,
    );

    public readonly uriPath$ = this.select(this.applicationLog$, ({ uri_path }) => uri_path);

    // ****************************** Application Log Selectors **********************************

    public readonly responseCode$ = this.select(
        this.applicationLog$,
        ({ response_code }) => response_code,
    );

    public readonly serverResponseCode$ = this.select(
        this.applicationLog$,
        ({ server_response_code }) => server_response_code,
    );

    public readonly appResponseTime$ = this.select(
        this.applicationLog$,
        ({ app_response_time }) => (
            app_response_time ? Number(app_response_time) : app_response_time
        ),
    );

    public readonly dataTransferTime$ = this.select(
        this.applicationLog$,
        ({ data_transfer_time }) => (
            data_transfer_time ? Number(data_transfer_time) : data_transfer_time
        ),
    );

    // TODO: need to confirm what comes as source IP port
    public readonly sourceIps$ = this.select(
        this.applicationLog$,
        ({ source_ip, source_ip6 }) => {
            return {
                source_ip,
                source_ip6,
            };
        },
    );

    public readonly ssl$ = this.select(
        this.applicationLog$,
        ({
            ssl_version,
            sni_hostname,
            ocsp_status_resp_sent,
            ssl_cipher,
            client_fingerprints,
            client_cipher_list,
        }) => {
            const ssl = {
                ssl_version,
                sni_hostname,
                ocsp_status_resp_sent,
                ssl_cipher,
                client_fingerprints,
                client_cipher_list,
            };

            if (isObjectEmpty(ssl)) {
                return null;
            }

            return ssl;
        },
    );

    public readonly httpRequestHeaders$ = this.select(
        this.applicationLog$,
        ({
            all_request_headers,
            headers_sent_to_server,
        }) => {
            const httpResponseHeaders = {
                all_request_headers,
                headers_sent_to_server,
            };

            if (isObjectEmpty(httpResponseHeaders)) {
                return null;
            }

            return httpResponseHeaders;
        },
    );

    public readonly httpResponseHeaders$ = this.select(
        this.applicationLog$,
        ({
            all_response_headers,
            headers_received_from_server,
        }) => {
            const httpResponseHeaders = {
                all_response_headers,
                headers_received_from_server,
            };

            if (isObjectEmpty(httpResponseHeaders)) {
                return null;
            }

            return httpResponseHeaders;
        },
    );

    public readonly applicationInfo$ = this.select(
        this.applicationLog$,
        ({
            http2_stream_id,
            server_push_initiated,
            server_pushed_request,
            cache_hit,
            compression,
            compression_percentage,
            servers_tried,
            method,
        }) => {
            const applicationInfo = {
                http2_stream_id,
                server_push_initiated,
                server_pushed_request,
                cache_hit,
                compression,
                compression_percentage,
                servers_tried,
                method,
            };

            if (isObjectEmpty(applicationInfo)) {
                return null;
            }

            return applicationInfo;
        },
    );

    public readonly grpcInfo$ = this.select(
        this.applicationLog$,
        ({
            grpc_service_name,
            grpc_method_name,
            grpc_status_reason_phrase,
        }) => {
            const grpcInfo = {
                grpc_service_name,
                grpc_method_name,
                grpc_status_reason_phrase,
            };

            if (isObjectEmpty(grpcInfo)) {
                return null;
            }

            return grpcInfo;
        },
    );

    public readonly datascriptInfo$ = this.select(
        this.applicationLog$,
        this.connectionLog$,
        (
            { datascript_error_trace, datascript_log },
            { ds_log },
        ) => {
            const datascriptInfo = {
                datascript_error_trace,
                datascript_log,
                ds_log,
            };

            if (isObjectEmpty(datascriptInfo)) {
                return null;
            }

            return datascriptInfo;
        },
    );

    public readonly wafAllowListLogs$ = this.select(
        this.applicationLog$,
        ({ waf_log }) => waf_log?.allowlist_logs,
    );

    public readonly wafLog$ = this.select(
        this.applicationLog$,
        ({ waf_log }) => waf_log,
    );

    public readonly wafPsmLogs$ = this.select(
        this.applicationLog$,
        ({ waf_log }) => waf_log?.psm_logs,
    );

    public readonly wafStatus$ = this.select(
        this.applicationLog$,
        ({ waf_log }) => waf_log?.status,
    );

    /** Datascript request logs under out of band log */
    public readonly oobDsRequestLogs$ = this.select(
        this.applicationLog$,
        ({ oob_log }) => oob_log?.ds_req_logs,
    );

    public readonly samlLog$ = this.select(
        this.applicationLog$,
        ({ saml_log }) => saml_log,
    );

    public readonly oAuthLog$ = this.select(
        this.applicationLog$,
        ({ oauth_log }) => oauth_log,
    );

    public readonly icapLog$ = this.select(
        this.applicationLog$,
        ({ icap_log }) => icap_log,
    );

    public readonly jwtLog$ = this.select(
        this.applicationLog$,
        ({ jwt_log }) => jwt_log,
    );

    public readonly ntlmLog$ = this.select(
        this.applicationLog$,
        ({ ntlm_log }) => ntlm_log,
    );

    // return true if one of the authentication logs is present
    public readonly hasAuthenticationLogs$ = this.select(
        this.samlLog$,
        this.oAuthLog$,
        this.jwtLog$,
        this.ntlmLog$,
        (samlLog, oAuthLog, jwtLog, ntlmLog) => {
            return Boolean(samlLog || oAuthLog || jwtLog || ntlmLog);
        },
    );

    public readonly wafApplicationRuleLogs$ = this.select(
        this.applicationLog$,
        ({ waf_log }): IWafRuleLog[] => waf_log?.application_rule_logs,
    );

    public readonly wafRuleLogs$ = this.select(
        this.applicationLog$,
        ({ waf_log }): IWafRuleLog[] => waf_log?.rule_logs,
    );

    public readonly wafRuleLogsByGroup$ = this.select(
        this.wafRuleLogs$,
        (ruleLogs: IWafRuleLog[] = []): TWafRuleLogsByGroup => {
            return ruleLogs.reduce((output, ruleLog, index) => {
                const { rule_group: ruleGroup = `__N/A:${index}` } = ruleLog;

                output[ruleGroup] = output[ruleGroup] || [];
                output[ruleGroup].push(ruleLog);

                return output;
            }, {} as any as TWafRuleLogsByGroup);
        },
    );

    public readonly botManagementLog$ = this.select(
        this.applicationLog$,
        ({ bot_management_log }) => bot_management_log,
    );

    // ****************************** Connection Log Selectors **********************************
    public readonly gslbService$ = this.select(
        this.connectionLog$,
        ({ gslbservice }) => gslbservice,
    );

    public readonly dnsRequestInfo$ = this.select(
        this.connectionLog$,
        ({ dns_request }) => dns_request,
    );

    public readonly dnsResponseInfo$ = this.select(
        this.connectionLog$,
        ({ dns_response }) => dns_response,
    );

    // ************************************ L4 Log Selectors **************************************

    public readonly l4ClientConnectionInfo$ = this.select(
        this.connectionLog$,
        ({
            tx_bytes: transmittedBytes,
            tx_pkts: transmittedPackets,
            rx_bytes: receivedBytes,
            rx_pkts: receivedPackets,
            total_bytes: totalBytes,
            total_pkts: totalPackets,
            out_of_orders: outOfOrders,
            timeouts,
            retransmits,
        }) => {
            const l4ClientConnectionInfo = {
                transmittedBytes,
                transmittedPackets,
                receivedBytes,
                receivedPackets,
                totalBytes,
                totalPackets,
                outOfOrders,
                timeouts,
                retransmits,
            };

            if (isObjectEmpty(l4ClientConnectionInfo)) {
                return null;
            }

            return l4ClientConnectionInfo;
        },
    );

    public readonly l4ServerConnectionInfo$ = this.select(
        this.connectionLog$,
        ({
            server_tx_bytes: transmittedBytes,
            server_tx_pkts: transmittedPackets,
            server_rx_bytes: receivedBytes,
            server_rx_pkts: receivedPackets,
            server_total_bytes: totalBytes,
            server_total_pkts: totalPackets,
            server_out_of_orders: outOfOrders,
            server_timeouts: timeouts,
            server_retransmits: retransmits,
        }) => {
            const l4ServerConnectionInfo = {
                transmittedBytes,
                transmittedPackets,
                receivedBytes,
                receivedPackets,
                totalBytes,
                totalPackets,
                outOfOrders,
                timeouts,
                retransmits,
            };

            if (isObjectEmpty(l4ServerConnectionInfo)) {
                return null;
            }

            return l4ServerConnectionInfo;
        },
    );

    // *************************************** Overrides ****************************************

    /**
     * Implementation of the parent class abstract closeCinematic$ Subject.
     * Subject to close the cinematic view.
     */
    public override readonly closeCinematic$ = new Subject<void>();

    protected readonly storeDestroySubject$ = new Subject<void>();

    protected override readonly logUpdateSubscription = this.logListStore.recentlyViewedLog$
        .pipe(takeUntil(this.storeDestroySubject$))
        .subscribe(log => {
            this.setLogBasedOnType(log);
        });

    protected override neighborLogFlagsSubscription = combineLatest([
        this.logListStore.recentlyViewedLogIndex$,
        this.logsStore.logListPaginationInfo$,
    ])
        .pipe(takeUntil(this.storeDestroySubject$))
        .subscribe(
            ([currentIndex, logListPaginationInfo]) => this.setNeighborLogFlags(currentIndex, logListPaginationInfo),
        );

    // ************************************* Constructor *************************************

    constructor(
        protected readonly logListStore: TrafficLogListStore,
        logsStore: TrafficLogsStore<ITrafficLogsStates>,
    ) {
        super(logsStore, initialState);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.storeDestroySubject$.next();
        this.storeDestroySubject$.complete();
        this.closeCinematic$.complete();
    }
}
